import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MSUser } from '../types/types';
import { GroovUser, RoleType, Schemas } from '../types/GroovUser';
import { createUser, getUser, getUserRoles } from '../tab/api';
import config from '@config/config';
import { useTeamsUserCredential } from '@microsoft/teamsfx-react';
import { initBasicAuth } from './auth';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

type CreateGroovUserProps = {
  userId: string;
  displayName: string;
  email: string;
  tenantId: string;
};

const createGroovUser = async ({ userId, displayName, email, tenantId }: CreateGroovUserProps) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  await createUser(tenantId, userId, displayName, email, timezone);
};

const getGroovUser = async (msUser: MSUser) => {
  const userId = msUser.objectId;
  let userResponse;
  try {
    userResponse = await getUser(userId);
  } catch (error: unknown) {
    // If user is not found, attempt to create a new user
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      await createGroovUser({
        userId,
        displayName: msUser.displayName,
        email: msUser.preferredUserName,
        tenantId: msUser.tenantId,
      });
      userResponse = await getUser(userId);
    } else {
      throw error;
    }
  }
  const userResult = Schemas.GroovUserDTOSchema.safeParse(userResponse);
  if (!userResult.success) {
    throw new Error('Failed to parse user response');
  }
  const groovUserDTO = userResult.data;

  const userRolesResponse = await getUserRoles(userId);
  const userRolesResult = Schemas.UserRolesDTOSchema.safeParse(userRolesResponse);
  if (!userRolesResult.success) {
    throw new Error('Failed to parse user roles response');
  }
  const userRolesDTO = userRolesResult.data;

  const groovUserResult = Schemas.GroovUserSchema.safeParse({
    ...groovUserDTO,
    roles: userRolesDTO.roles,
  });
  if (!groovUserResult.success) {
    throw new Error('Failed to parse groov user');
  }
  return groovUserResult.data satisfies GroovUser;
};

interface IAuthContext {
  isLoading: boolean;
  groovUser: GroovUser | undefined;
  userTheme?: string;
  isError: boolean;
  hasAccess: (roles: RoleType[]) => boolean;
  refresh: () => Promise<void>;
}

const initialAuthContext: IAuthContext = {
  isLoading: true,
  groovUser: undefined,
  userTheme: undefined,
  isError: false,
  hasAccess: () => false,
  refresh: async () => {},
};

const AuthContext = createContext<IAuthContext>(initialAuthContext);

export const useUser = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useUserContext must be used within an UserProvider');
  }

  return context;
};

const demoUser: MSUser = {
  displayName: 'Demo User',
  objectId: config.demoUserId,
  preferredUserName: '',
  tenantId: 'demo',
};

export const AuthContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const location = useLocation();
  const { themeString: userTheme, teamsUserCredential } = useTeamsUserCredential({
    initiateLoginEndpoint: config.initiateLoginEndpoint!,
    clientId: config.clientId!,
  });

  const [groovUser, setGroovUser] = useState<GroovUser | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  const isDemoMode = location.pathname.startsWith('/demo');

  const getUser = useCallback(async () => {
    setIsLoading(true);

    if (!teamsUserCredential && !isDemoMode) return;

    try {
      const msUser = isDemoMode ? demoUser : await teamsUserCredential!.getUserInfo();

      await initBasicAuth(msUser.objectId);
      const user = await getGroovUser(msUser);
      setGroovUser(user);
      setIsError(false);
    } catch (error: unknown) {
      setIsError(true);
      setGroovUser(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [teamsUserCredential, isDemoMode]);

  const hasAccess = useCallback(
    (roles: RoleType[]) => {
      if (!roles || roles.length === 0) {
        return true;
      }

      if (!groovUser) {
        return false;
      }

      const roleMap: Record<RoleType, string> = {
        Leader: config.leadRoleId,
        Manager: config.managerRoleId,
      };

      return roles.some((role) =>
        groovUser.roles.some((userRole) => userRole.id === roleMap[role])
      );
    },
    [groovUser]
  );

  useEffect(() => {
    getUser();
  }, [getUser]);

  const value = useMemo(
    () => ({
      isLoading,
      groovUser,
      userTheme,
      isError,
      hasAccess,
      refresh: getUser,
    }),
    [isLoading, groovUser, userTheme, isError, hasAccess, getUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
